import { useEffect, useState } from "react";

import "./importar-comic.scss";
import Nav from "../../components/nav";
import ConfirmPopUp, { PopUpInfo } from "../../components/confirm-pop-up";
import AppUser from "../../api/app-user.class";
import { Comic } from "../../classes/comic.class";
import {
  AJUDA_ESTADO,
  ROTULO_ESTADO,
  SituacaoDoItem,
  situacoesDosItens,
} from "../meus-quadrinhos/estado-publicacao";

import Avatar from "../../assets/dashboard/dashboard-avatar.png";

/**
 * O ACERVO RECEBE NAVEGACAO, NAO `setState`.
 *
 * Antes eram `setCurrentComic(comic.copy())` para abrir e `setCurrentComic(undefined)` para
 * voltar aos baloes: esta tela empurrava estado para o componente pai, que era o unico que
 * tinha endereco. Era o T2.45 do pedido visto de dentro — "apenas uma conta dashboard para
 * tudo complica; tem que ser rotas para cada um".
 *
 * Agora cada acao e um destino, e quem sabe o endereco e a rota (`acervo.tsx`). Esta tela
 * voltou a fazer uma coisa so: mostrar o que a pessoa criou.
 */
type propsType = {
  appUser: AppUser;
  aoCriarNovo: () => void;
  aoVer: (comic: Comic) => void;
  aoEditar: (comic: Comic) => void;
  /** T2.53 — e o ato que nao existia. Ver `publicar/publicar-quadrinho.tsx`. */
  aoPublicar: (comic: Comic) => void;
  /** Quem acabou de publicar volta para ca, e precisa ver que deu certo. */
  publicou?: { titulo: string; publicado: boolean; finalizado: boolean };
};

/**
 * A miniatura cabe numa caixa FIXA, preservando a proporcao da pagina.
 *
 * Antes o tamanho saia de `window.innerWidth * 0.2`, recalculado por um listener de `resize` que
 * nunca era removido — um listener a mais por montagem, para sempre. E o resultado dependia da
 * largura da janela: a mesma tirinha aparecia enorme num monitor e minuscula noutro, com o
 * espacamento (`margin: 0.7vw 3vw`) escorrendo junto. E a origem do "espacamento ruim" do T1.29.
 *
 * O piso de 8px existe pelo mesmo motivo do dashboard: o webtoon e 1:5, e sem piso a largura
 * proporcional vira um risco em vez de um retangulo.
 */
const CAIXA = { largura: 190, altura: 240 };

function encaixar(largura: number, altura: number) {
  const escala = Math.min(CAIXA.largura / largura, CAIXA.altura / altura);
  return {
    width: Math.max(Math.round(largura * escala), 8),
    height: Math.max(Math.round(altura * escala), 8),
  };
}

function ImportComic(props: propsType) {
  const { appUser, aoCriarNovo, aoVer, aoEditar, aoPublicar, publicou } = props;

  const [popUpInfo, setPopUpInfo] = useState<undefined | PopUpInfo>(undefined);

  const quadrinhos = appUser.Comics();

  /**
   * O ESTADO DE PUBLICACAO DE CADA ITEM — T2.25, e e a metade do ponto que costuma passar batido.
   *
   * "voce ate definiu QUAL DESSE SEU ACERVO E PUBLICO, que cai nessa biblioteca, ou que nao seja
   *  publico (...) E QUAL DESSE MATERIAL ELE E SEU ACERVO PESSOAL."
   *
   * Uma lista sem isso responde "o que eu criei" e nao responde "o que dele as outras pessoas
   * veem" — e e a segunda que ele cobra. Ver `estado-publicacao.ts` para os tres estados e para
   * por que isto e uma leitura a mais em vez de um campo duplicado no quadrinho.
   */
  const [situacoes, setSituacoes] = useState<Record<string, SituacaoDoItem>>({});

  useEffect(() => {
    let vivo = true;
    situacoesDosItens(quadrinhos.map((c) => c.getId())).then((s) => {
      if (vivo) setSituacoes(s);
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quadrinhos.length]);

  return (
    <div className="importar-comic-container">
      {popUpInfo && <ConfirmPopUp {...popUpInfo} />}

      {/* A INTERCEPCAO DE "MINHA CONTA" MORREU AQUI, e nao foi limpeza: ela so existia porque
          esta tela nao tinha endereco. Estando dentro do `/dashboard`, clicar em "Minha conta"
          precisava de um `setState` para sair do estado da lista antes de navegar. Com rota
          propria, `/minha-conta` e um link que funciona sozinho — inclusive em aba nova, que a
          intercepcao quebrava. */}
      <Nav />

      <div className="content-container">
        {/* A tela nao tinha titulo nenhum. O `.header-container` existia no SCSS e nao era usado
            por marcacao alguma — CSS morto de uma versao anterior. Quem chegava aqui via dois
            baloes soltos e nao sabia que estava em "Meus quadrinhos". */}
        {/* O RETORNO DE QUEM PUBLICOU.
            Sem isto, publicar termina numa tela igual a de antes e o unico jeito de saber que
            funcionou e abrir a biblioteca e procurar. E a mesma familia de defeito do T2.17
            ("nada acontece"): a acao acontece e a tela nao conta.

            O texto diz as DUAS decisoes, porque foram duas chavinhas (T2.56) — e quem marcou
            "pronto" precisa ouvir de volta que aquilo nao tem volta. */}
        {publicou && (
          <p className="mq-publicou" role="status">
            <strong>“{publicou.titulo}” foi publicado.</strong>{" "}
            {publicou.publicado
              ? "Ele já está na biblioteca para as outras pessoas lerem."
              : "Ele ficou guardado só para você — dá para publicar depois."}{" "}
            {publicou.finalizado
              ? "Está marcado como pronto, então nem você pode editá-lo agora."
              : "Continua em construção: dá para editar e publicar de novo."}
          </p>
        )}

        <header className="mq-cabecalho">
          <div>
            <h1>Meus quadrinhos</h1>
            <p className="mq-contagem">
              {quadrinhos.length === 0
                ? "Nenhum quadrinho salvo ainda."
                : quadrinhos.length === 1
                ? "1 quadrinho salvo."
                : `${quadrinhos.length} quadrinhos salvos.`}
            </p>
          </div>

          {/* Era `Comic.getEmptyComic()` — sem argumento, ou seja, uma tirinha 1200×400 SEM
              grade e sem ninguem ter escolhido formato. Era um segundo caminho de criacao que
              driblava o seletor de formato, e o pior dos dois. Agora leva a tela onde se
              escolhe, que e a mesma para todo mundo. */}
          <button type="button" className="mq-novo" onClick={aoCriarNovo}>
            Novo quadrinho
          </button>
        </header>

        {quadrinhos.length === 0 ? (
          <div className="mq-vazio">
            <img src={Avatar} alt="" aria-hidden="true" />
            <p>
              Quando você salvar um quadrinho, ele fica guardado aqui — e dá
              para voltar a editar quando quiser.
            </p>
          </div>
        ) : (
          <ul id="comics-container" className="comics-container">
            {quadrinhos.map((comic) => {
              const primeira = comic.getPage(0);
              const tamanho = encaixar(primeira.width, primeira.height);
              const paginas = comic.Pages().length;

              return (
                <li key={comic.getId()} className="comic-container">
                  {/* AS DUAS OPCOES DO T2.50, e elas substituem o alvo unico.
                      "Ele tem que me dar a opcao de ou eu ver esse quadrinho simplesmente,
                       visualizar como um quadrinho mesmo, ou eu editar ele. Porque as unicas
                       opcoes que eu tenho hoje e editar ele."  [T2.43]

                      O cartao tinha UM botao invisivel esticado por cima, e ele abria o editor.
                      Duas acoes nao cabem nesse desenho: um alvo unico nao pode significar duas
                      coisas. Entao a miniatura vira o caminho de LER — que e o destino menos
                      destrutivo dos dois — e as duas acoes ganham rotulo visivel embaixo.

                      A miniatura continua com `pointer-events: none` para que o clique nao pare
                      no canvas do Konva, e o botao sobreposto continua sendo um `button` real:
                      chega por Tab, dispara com Enter, e e anunciado. */}
                  <div className="mq-cartao">
                    <span
                      className="mq-miniatura"
                      style={{ width: CAIXA.largura, height: CAIXA.altura }}
                    >
                      {comic.renderPage(0, { ...tamanho, viewOnly: true })}
                    </span>

                    <span className="mq-nome">{comic.Name()}</span>
                    <span className="mq-paginas">
                      {paginas === 1 ? "1 página" : `${paginas} páginas`}
                    </span>

                    {/* O SELO DO T2.25. Dois selos e nao um: o eixo de quem VE e o eixo de
                        ainda MUDA sao independentes (T2.61), e um selo so precisaria escolher
                        qual dos dois esconder.

                        `title` carrega a explicacao inteira, e o texto visivel e curto porque
                        cabe num cartao de 210px. Cor NUNCA sozinha: o rotulo diz. */}
                    {(() => {
                      const id = comic.getId();
                      const sit = id ? situacoes[id] : undefined;
                      if (!sit) return null;
                      return (
                        <span className="mq-selos">
                          <span
                            className={`mq-selo mq-selo-${sit.estado}`}
                            title={AJUDA_ESTADO[sit.estado]}
                          >
                            {ROTULO_ESTADO[sit.estado]}
                          </span>
                          {sit.finalizado && (
                            <span
                              className="mq-selo mq-selo-pronto"
                              title="Marcado como pronto: nem você pode editar. Para mudar, exclua e publique outro."
                            >
                              pronto
                            </span>
                          )}
                        </span>
                      );
                    })()}

                    <button
                      type="button"
                      className="mq-abrir"
                      aria-label={`Ver o quadrinho ${comic.Name()}`}
                      onClick={() => aoVer(comic)}
                    />

                    <span className="mq-acoes">
                      <button
                        type="button"
                        className="mq-acao mq-acao-ver"
                        onClick={() => aoVer(comic)}
                      >
                        Ver
                      </button>
                      {/* EDITAR DESAPARECE PARA O QUE ESTA FINALIZADO.
                          A regra do Firestore nega o update de um finalizado, inclusive para o
                          autor (T2.58, T2.60). Oferecer o botao faria a pessoa clicar, editar,
                          salvar e receber "negado" — depois do trabalho. A tela nao deve oferecer
                          o que a regra recusa.

                          E a saida continua visivel ao lado: excluir (D2). */}
                      {!(comic.getId() && situacoes[comic.getId()!]?.finalizado) && (
                        <button
                          type="button"
                          className="mq-acao mq-acao-editar"
                          onClick={() => aoEditar(comic)}
                        >
                          Editar
                        </button>
                      )}
                      <button
                        type="button"
                        className="mq-acao mq-acao-publicar"
                        onClick={() => aoPublicar(comic)}
                      >
                        Publicar
                      </button>
                    </span>
                  </div>

                  {/* Era um `<span>X</span>` vermelho, solto acima do cartao, sem nome
                      acessivel: um leitor de tela anunciava a letra "X". Agora e um botao com
                      rotulo que diz de QUAL quadrinho se trata — a confirmacao ja existia e
                      continua a mesma. */}
                  <button
                    type="button"
                    className="mq-excluir"
                    aria-label={`Excluir o quadrinho ${comic.Name()}`}
                    title={`Excluir o quadrinho ${comic.Name()}`}
                    onClick={() =>
                      setPopUpInfo({
                        type: "confirm",
                        // T8.26 · sem o espaco antes do "?", e com o botao dizendo o que faz em vez
                        // de "Sim". Num dialogo que APAGA, o rotulo do botao e a ultima chance de a
                        // pessoa perceber o que vai acontecer.
                        text: `Excluir o quadrinho "${comic.Name()}"?`,
                        rotuloConfirmar: "Excluir",
                        close: () => setPopUpInfo(undefined),
                        confirm: async () => {
                          comic
                            .delete(appUser.Id())
                            .then(() => {
                              setPopUpInfo({
                                type: "alert",
                                text: "Quadrinho excluído.",
                                close: () => setPopUpInfo(undefined),
                                confirm: () => setPopUpInfo(undefined),
                              });
                            })
                            .catch((err) => {
                              console.error(
                                "[meus-quadrinhos] falha ao excluir",
                                err
                              );
                              setPopUpInfo({
                                type: "alert",
                                text: "Não foi possível excluir o quadrinho.",
                                close: () => setPopUpInfo(undefined),
                                confirm: () => setPopUpInfo(undefined),
                              });
                            });
                        },
                      })
                    }
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ImportComic;
